/**
 * VanillaSlider - Lightweight slider replacement for Slick
 * No jQuery dependency - Pure vanilla JavaScript
 */

(function() {
    'use strict';

    class VanillaSlider {
        constructor(element, options = {}) {
            this.container = typeof element === 'string' ? document.querySelector(element) : element;
            if (!this.container) return;

            // Default options
            this.defaults = {
                slidesToShow: 1,
                slidesToScroll: 1,
                infinite: false,
                autoplay: false,
                autoplaySpeed: 3000,
                speed: 500,
                dots: false,
                arrows: true,
                fade: false,
                swipe: true,
                adaptiveHeight: false,
                pauseOnHover: true,
                responsive: [],
                prevArrow: '<button class="vanilla-slider-prev" aria-label="Previous"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="20" height="20"><path d="M65.75 90c.896 0 1.792-.342 2.475-1.025 1.367-1.366 1.367-3.583 0-4.949L29.2 45 68.225 5.975c1.367-1.367 1.367-3.583 0-4.95-1.367-1.366-3.583-1.366-4.95 0l-41.5 41.5c-1.367 1.366-1.367 3.583 0 4.949l41.5 41.5c.683.683 1.579 1.025 2.475 1.025z" fill="currentColor"/></svg></button>',
                nextArrow: '<button class="vanilla-slider-next" aria-label="Next"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="20" height="20"><path d="M24.25 90c-.896 0-1.792-.342-2.475-1.025-1.367-1.366-1.367-3.583 0-4.949L60.8 45 21.775 5.975c-1.367-1.367-1.367-3.583 0-4.95 1.367-1.366 3.583-1.366 4.95 0l41.5 41.5c1.367 1.366 1.367 3.583 0 4.949l-41.5 41.5C26.042 89.658 25.146 90 24.25 90z" fill="currentColor"/></svg></button>'
            };

            // Parse data-slick attribute if present
            const dataSlick = this.container.dataset.slick;
            let dataOptions = {};
            if (dataSlick) {
                try {
                    dataOptions = JSON.parse(dataSlick);
                } catch (e) {
                    console.warn('VanillaSlider: Invalid data-slick JSON');
                }
            }

            // Merge options
            this.options = Object.assign({}, this.defaults, dataOptions, options);
            this.currentOptions = Object.assign({}, this.options);

            // State
            this.currentSlide = 0;
            this.slideCount = 0;
            this.autoplayTimer = null;
            this.isTransitioning = false;
            this.touchStartX = 0;
            this.touchEndX = 0;
            this.isDragging = false;

            this.init();
        }

        init() {
            // Get slides
            this.slides = Array.from(this.container.children);
            this.slideCount = this.slides.length;

            if (this.slideCount === 0) return;

            // Build structure
            this.buildSlider();
            this.applyResponsive();
            this.bindEvents();
            this.goTo(0, false);

            if (this.currentOptions.autoplay) {
                this.startAutoplay();
            }

            this.container.classList.add('vanilla-slider-initialized');
        }

        buildSlider() {
            // Wrap slides in track
            this.container.classList.add('vanilla-slider');

            this.track = document.createElement('div');
            this.track.className = 'vanilla-slider-track';

            this.slides.forEach(slide => {
                slide.classList.add('vanilla-slider-slide');
                this.track.appendChild(slide);
            });

            this.container.appendChild(this.track);

            // Add arrows
            if (this.currentOptions.arrows && this.slideCount > this.currentOptions.slidesToShow) {
                this.buildArrows();
            }

            // Add dots
            if (this.currentOptions.dots && this.slideCount > this.currentOptions.slidesToShow) {
                this.buildDots();
            }
        }

        buildArrows() {
            const prevArrow = this.currentOptions.prevArrow;
            const nextArrow = this.currentOptions.nextArrow;

            // Create wrapper for arrows
            this.arrowsWrapper = document.createElement('div');
            this.arrowsWrapper.className = 'vanilla-slider-arrows';

            // Parse arrows
            const tempDiv = document.createElement('div');

            tempDiv.innerHTML = prevArrow;
            this.prevButton = tempDiv.firstElementChild;
            this.prevButton.classList.add('vanilla-slider-arrow', 'vanilla-slider-prev');

            tempDiv.innerHTML = nextArrow;
            this.nextButton = tempDiv.firstElementChild;
            this.nextButton.classList.add('vanilla-slider-arrow', 'vanilla-slider-next');

            this.container.appendChild(this.prevButton);
            this.container.appendChild(this.nextButton);
        }

        buildDots() {
            const dotCount = Math.ceil((this.slideCount - this.currentOptions.slidesToShow) / this.currentOptions.slidesToScroll) + 1;

            this.dotsWrapper = document.createElement('ul');
            this.dotsWrapper.className = 'vanilla-slider-dots';

            for (let i = 0; i < dotCount; i++) {
                const dot = document.createElement('li');
                dot.innerHTML = '<button type="button" aria-label="Go to slide ' + (i + 1) + '">' + (i + 1) + '</button>';
                if (i === 0) dot.classList.add('vanilla-slider-active');
                this.dotsWrapper.appendChild(dot);
            }

            this.container.appendChild(this.dotsWrapper);
        }

        bindEvents() {
            // Arrow clicks
            if (this.prevButton) {
                this.prevButton.addEventListener('click', () => this.prev());
            }
            if (this.nextButton) {
                this.nextButton.addEventListener('click', () => this.next());
            }

            // Dot clicks
            if (this.dotsWrapper) {
                this.dotsWrapper.addEventListener('click', (e) => {
                    const dot = e.target.closest('li');
                    if (dot) {
                        const index = Array.from(this.dotsWrapper.children).indexOf(dot);
                        this.goTo(index * this.currentOptions.slidesToScroll);
                    }
                });
            }

            // Touch/swipe events
            if (this.currentOptions.swipe) {
                this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
                this.track.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: true });
                this.track.addEventListener('touchend', (e) => this.onTouchEnd(e));

                // Mouse drag
                this.track.addEventListener('mousedown', (e) => this.onMouseDown(e));
                document.addEventListener('mousemove', (e) => this.onMouseMove(e));
                document.addEventListener('mouseup', (e) => this.onMouseUp(e));
            }

            // Pause on hover
            if (this.currentOptions.pauseOnHover && this.currentOptions.autoplay) {
                this.container.addEventListener('mouseenter', () => this.stopAutoplay());
                this.container.addEventListener('mouseleave', () => this.startAutoplay());
            }

            // Responsive
            window.addEventListener('resize', () => this.onResize());
        }

        onTouchStart(e) {
            this.touchStartX = e.touches[0].clientX;
            this.isDragging = true;
        }

        onTouchMove(e) {
            if (!this.isDragging) return;
            this.touchEndX = e.touches[0].clientX;
        }

        onTouchEnd(e) {
            if (!this.isDragging) return;
            this.isDragging = false;
            this.handleSwipe();
        }

        onMouseDown(e) {
            e.preventDefault();
            this.touchStartX = e.clientX;
            this.isDragging = true;
            this.track.style.cursor = 'grabbing';
        }

        onMouseMove(e) {
            if (!this.isDragging) return;
            this.touchEndX = e.clientX;
        }

        onMouseUp(e) {
            if (!this.isDragging) return;
            this.isDragging = false;
            this.track.style.cursor = '';
            this.handleSwipe();
        }

        handleSwipe() {
            const diff = this.touchStartX - this.touchEndX;
            const threshold = 50;

            if (Math.abs(diff) > threshold) {
                if (diff > 0) {
                    this.next();
                } else {
                    this.prev();
                }
            }

            this.touchStartX = 0;
            this.touchEndX = 0;
        }

        onResize() {
            this.applyResponsive();
            this.goTo(this.currentSlide, false);
        }

        applyResponsive() {
            const width = window.innerWidth;
            let newOptions = Object.assign({}, this.options);

            // Sort breakpoints descending
            const breakpoints = [...this.options.responsive].sort((a, b) => b.breakpoint - a.breakpoint);

            for (const bp of breakpoints) {
                if (width < bp.breakpoint && bp.settings) {
                    newOptions = Object.assign({}, newOptions, bp.settings);
                    break;
                }
            }

            // Check if slidesToShow changed
            if (this.currentOptions.slidesToShow !== newOptions.slidesToShow) {
                this.currentOptions = newOptions;
                this.updateSlideWidth();
                this.updateArrowsVisibility();
                this.updateDotsVisibility();
            } else {
                this.currentOptions = newOptions;
            }
        }

        updateSlideWidth() {
            const slideWidth = 100 / this.currentOptions.slidesToShow;
            this.slides.forEach(slide => {
                slide.style.width = slideWidth + '%';
                slide.style.flexShrink = '0';
            });
        }

        updateArrowsVisibility() {
            if (this.prevButton && this.nextButton) {
                const show = this.slideCount > this.currentOptions.slidesToShow;
                this.prevButton.style.display = show ? '' : 'none';
                this.nextButton.style.display = show ? '' : 'none';
            }
        }

        updateDotsVisibility() {
            if (this.dotsWrapper) {
                const show = this.slideCount > this.currentOptions.slidesToShow;
                this.dotsWrapper.style.display = show ? '' : 'none';
            }
        }

        goTo(index, animate = true) {
            if (this.isTransitioning) return;

            const maxIndex = Math.max(0, this.slideCount - this.currentOptions.slidesToShow);

            // Clamp index
            if (this.currentOptions.infinite) {
                if (index < 0) index = maxIndex;
                if (index > maxIndex) index = 0;
            } else {
                index = Math.max(0, Math.min(index, maxIndex));
            }

            this.currentSlide = index;

            // Calculate position
            const slideWidth = 100 / this.currentOptions.slidesToShow;
            const position = -index * slideWidth;

            // Apply transition
            if (animate) {
                this.isTransitioning = true;
                this.track.style.transition = `transform ${this.currentOptions.speed}ms ease`;
                setTimeout(() => {
                    this.isTransitioning = false;
                    this.track.style.transition = '';
                }, this.currentOptions.speed);
            }

            if (this.currentOptions.fade) {
                this.slides.forEach((slide, i) => {
                    slide.style.opacity = i === index ? '1' : '0';
                    slide.style.zIndex = i === index ? '1' : '0';
                });
            } else {
                this.track.style.transform = `translateX(${position}%)`;
            }

            // Update dots
            this.updateDots();

            // Update arrows disabled state
            this.updateArrows();

            // Dispatch event
            this.container.dispatchEvent(new CustomEvent('afterChange', {
                detail: { currentSlide: this.currentSlide }
            }));
        }

        updateDots() {
            if (!this.dotsWrapper) return;

            const dots = this.dotsWrapper.querySelectorAll('li');
            const activeDot = Math.floor(this.currentSlide / this.currentOptions.slidesToScroll);

            dots.forEach((dot, i) => {
                dot.classList.toggle('vanilla-slider-active', i === activeDot);
            });
        }

        updateArrows() {
            if (!this.currentOptions.infinite) {
                const maxIndex = this.slideCount - this.currentOptions.slidesToShow;

                if (this.prevButton) {
                    this.prevButton.disabled = this.currentSlide === 0;
                    this.prevButton.classList.toggle('vanilla-slider-disabled', this.currentSlide === 0);
                }
                if (this.nextButton) {
                    this.nextButton.disabled = this.currentSlide >= maxIndex;
                    this.nextButton.classList.toggle('vanilla-slider-disabled', this.currentSlide >= maxIndex);
                }
            }
        }

        prev() {
            this.goTo(this.currentSlide - this.currentOptions.slidesToScroll);
        }

        next() {
            this.goTo(this.currentSlide + this.currentOptions.slidesToScroll);
        }

        startAutoplay() {
            if (this.autoplayTimer) return;

            this.autoplayTimer = setInterval(() => {
                this.next();
            }, this.currentOptions.autoplaySpeed);
        }

        stopAutoplay() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
        }

        destroy() {
            this.stopAutoplay();

            // Remove event listeners
            window.removeEventListener('resize', () => this.onResize());

            // Remove added elements
            if (this.prevButton) this.prevButton.remove();
            if (this.nextButton) this.nextButton.remove();
            if (this.dotsWrapper) this.dotsWrapper.remove();

            // Unwrap slides
            this.slides.forEach(slide => {
                slide.classList.remove('vanilla-slider-slide');
                slide.style.width = '';
                this.container.appendChild(slide);
            });

            this.track.remove();
            this.container.classList.remove('vanilla-slider', 'vanilla-slider-initialized');
        }
    }

    // Export to window
    window.VanillaSlider = VanillaSlider;

    // Auto-init for elements with data-vanilla-slider attribute
    document.addEventListener('DOMContentLoaded', function() {
        document.querySelectorAll('[data-vanilla-slider]').forEach(el => {
            new VanillaSlider(el);
        });
    });

})();
